import { useQuery, useMutation} from '@tanstack/react-query'
import { getAllBusinesses, submitBusinessDetailsForm, deleteBusinessDetails, getBusinessDetails, generateBusinessReadiness, getBusinessAssociatedResources, listComplianceSessionsForUser, updateBusinessMenu, updateBusinessShare, submitFocusGroupForm, submitFocusGroupSessionResponse, getFocusGroupData, getPastSessionResponse, getFocusGroupSessions } from '../api/crud'
import { BusinessReadiness, FocusGroupFormData, FocusGroupSessionResponseSchema, MasterMenuItem, MenuSource, MyBusinessSchema, QuestionType } from "../data/types";
import { useMemo } from 'react';
import moment from 'moment-timezone';
import {
    KitchenIcon,
    TrainingIcon,
    DigitalIcon,
    CommunityIcon,
    DreamIcon,
    SupplyIcon,
    FeedbackIcon,
    BusinessOperationsIcon,
    GrowthScalingIcon,
    FundingIcon,
    RegulatoryIcon
  } from '../icons/FocusGroupIcons';



  const iconMap: Record<string, React.ComponentType> = {
    'KitchenIcon': KitchenIcon,
    'TrainingIcon': TrainingIcon,
    'DigitalIcon': DigitalIcon,
    'CommunityIcon': CommunityIcon,
    'DreamIcon': DreamIcon,
    'SupplyIcon': SupplyIcon,
    'FeedbackIcon': FeedbackIcon,
    'BusinessOperationsIcon': BusinessOperationsIcon,
    'GrowthScalingIcon': GrowthScalingIcon,
    'FundingIcon': FundingIcon,
    'RegulatoryIcon': RegulatoryIcon
  };


const staleTime = 1000


export const useAllBusinesses = (uid: string) => {
    const {data: businesses, refetch: reloadAllBusinesses, isLoading: isLoadingAllBusinesses} = useQuery({ 
        queryKey: ['businesses', uid], 
        queryFn: async () => {
            const res = await getAllBusinesses(uid)
            return res ? res : []
        },
        staleTime,
        enabled: Boolean(uid)
    })
    return { isLoadingAllBusinesses, businesses, reloadAllBusinesses}
}

export const useBusinessDetails = (uid: string, id: string) => {
    const {data: businessDetails, refetch: reloadBusinessDetails, isLoading: isLoadingBusinessDetails} = useQuery({ 
        queryKey: ['businessDetails', uid, id], 
        queryFn: async () => {
            const res = await getBusinessDetails(uid, id)
            return res ? res : {}
        },
        staleTime,
        enabled: Boolean(uid && id)
    })
    return { isLoadingBusinessDetails, businessDetails, reloadBusinessDetails}
}

/** Loads pantry resources linked to a saved business profile. */
export const useBusinessAssociatedResources = (uid: string, businessId: string) => {
    const { data: resources, refetch: reloadBusinessAssociatedResources, isLoading: isLoadingBusinessAssociatedResources } = useQuery({
        queryKey: ['businessAssociatedResources', uid, businessId],
        queryFn: async () => {
            const res = await getBusinessAssociatedResources(uid, businessId)
            return res || []
        },
        staleTime,
        enabled: Boolean(uid && businessId),
    })

    return { resources, reloadBusinessAssociatedResources, isLoadingBusinessAssociatedResources }
}

/** Loads compliance-checker sessions for the signed-in member. */
export const useComplianceSessionsForUser = (uid: string) => {
    const { data: sessions, refetch: reloadComplianceSessions, isLoading: isLoadingComplianceSessions } = useQuery({
        queryKey: ['complianceSessions', uid],
        queryFn: async () => {
            const res = await listComplianceSessionsForUser(uid)
            return res || []
        },
        staleTime,
        enabled: Boolean(uid),
    })

    return { sessions, reloadComplianceSessions, isLoadingComplianceSessions }
}


export const useUpdateBusinessDetails = (uid: string) => {
    const {reloadAllBusinesses} = useAllBusinesses(uid)
    
    const addBusinessDetailsMutation = useMutation({ 
        mutationFn: async ({
            businessDetails,
            logoFile,
        }: {
            businessDetails: MyBusinessSchema
            logoFile?: File | null
        }) => {
            const res = await submitBusinessDetailsForm(uid, businessDetails, logoFile)
            await reloadAllBusinesses()
            return res
        },
    })

    const addBusinessDetails = async (businessDetails: MyBusinessSchema, logoFile?: File | null) => {
        return addBusinessDetailsMutation.mutateAsync({ businessDetails, logoFile })
    }

    const deleteBusinessDetailsMutation = useMutation({ 
        mutationFn: async (businessDetailsId: string) => {
            const res = await deleteBusinessDetails(uid, businessDetailsId)
            await reloadAllBusinesses()
            return res
        },
    })

    const removeBusinessDetails = async (businessDetailsId: string) => {
        return deleteBusinessDetailsMutation.mutateAsync(businessDetailsId)
    }

    const generateBusinessReadinessMutation = useMutation({
        mutationFn: async ({ businessId, force }: { businessId: string; force?: boolean }) =>
            generateBusinessReadiness(uid, businessId, force),
    })

    /** Request the dashboard-backed structured readiness profile for a saved business. */
    const createBusinessReadiness = async (
        businessId: string,
        force = false
    ): Promise<BusinessReadiness | null> =>
        generateBusinessReadinessMutation.mutateAsync({ businessId, force })

    const isLoadingSetupIntent = addBusinessDetailsMutation.isPending || deleteBusinessDetailsMutation.isPending
    return {
        addBusinessDetails,
        removeBusinessDetails,
        createBusinessReadiness,
        isGeneratingBusinessReadiness: generateBusinessReadinessMutation.isPending,
        isLoadingSetupIntent,
    }
}

/** Persists master-menu edits for a saved business profile. */
export const useUpdateBusinessMenu = (uid: string, businessId: string) => {
    const { reloadBusinessDetails } = useBusinessDetails(uid, businessId)

    const updateMenuMutation = useMutation({
        mutationFn: async ({
            items,
            sources,
        }: {
            items: MasterMenuItem[]
            sources: Array<{ source: MenuSource; label?: string; connection_status?: string }>
        }) => {
            const res = await updateBusinessMenu(uid, businessId, {
                items,
                sources,
                updated: new Date().toISOString(),
            })
            await reloadBusinessDetails()
            return res
        },
    })

    const updateMenu = async (
        items: MasterMenuItem[],
        sources: Array<{ source: MenuSource; label?: string; connection_status?: string }>
    ) => updateMenuMutation.mutateAsync({ items, sources })

    return {
        updateMenu,
        isUpdatingBusinessMenu: updateMenuMutation.isPending,
    }
}

/** Persists optional partner data-sharing consent for a saved business profile. */
export const useUpdateBusinessShare = (uid: string, businessId: string) => {
    const { reloadBusinessDetails } = useBusinessDetails(uid, businessId)

    const updateShareMutation = useMutation({
        mutationFn: async (share: Record<string, unknown>) => {
            const res = await updateBusinessShare(uid, businessId, share)
            await reloadBusinessDetails()
            return res
        },
    })

    const updateShare = async (share: Record<string, unknown>) => updateShareMutation.mutateAsync(share)

    return {
        updateShare,
        isUpdatingBusinessShare: updateShareMutation.isPending,
    }
}


export const useFocusGroupRegistration = (communityId: string) => {
    const mutation = useMutation({
        mutationFn: async (formData: FocusGroupFormData) => {
            return await submitFocusGroupForm(formData, communityId);
        }
    });

    const submitFocusGroupRegistration = async (formData: FocusGroupFormData) => {
        try {
            const result = await mutation.mutateAsync(formData);
            return result !== null;
        } catch (error) {
            console.error('Focus group submission error:', error);
            return false;
        }
    };

    return {
        submitFocusGroupRegistration,
        isLoading: mutation.isPending,
        isSuccess: mutation.isSuccess,
        isError: mutation.isError,
        error: mutation.error
    };
};


export const useFocusGroupRegistrationData = (uid: string, communityId: string) => {
    const { data: focusGroupData, isLoading, isError, error } = useQuery({
        queryKey: ['focusGroup', uid, communityId],
        queryFn: async () => {
            return await getFocusGroupData(uid, communityId);
        },
        enabled: !!uid,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });

    return {
        focusGroupData: focusGroupData as FocusGroupFormData,
        isLoading,
        isError,
        error
    };
};


export const useFocusGroupSession = (uid: string, sessionId: string) => {
    const { data: pastSessionResponse, isLoading: isLoadingPastSessionResponse, isError: isErrorPastSessionResponse, error: errorPastSessionResponse, refetch: reloadPastSessionResponse } = useQuery({
        queryKey: ['pastSessionResponse', uid, sessionId],
        queryFn: async () => {
            return await getPastSessionResponse(uid, sessionId);
        },
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
    const submitFocusGroupSessionMutation = useMutation({
        mutationFn: async (responses: FocusGroupSessionResponseSchema) => {
            const response = await submitFocusGroupSessionResponse(uid, sessionId as string, responses);
            return response;
        },
        onSuccess: () => {
            reloadPastSessionResponse()
        },
    });

    const submitFocusGroupSession = async (responses: FocusGroupSessionResponseSchema) => {
        try {
            const result = await submitFocusGroupSessionMutation.mutateAsync(responses);
            return result ? result : null;
        } catch (error) {
            console.error('Focus group session submission error:', error);
            return null;
        }
    };

    const isLoading = isLoadingPastSessionResponse || submitFocusGroupSessionMutation.isPending
    const isSuccess = submitFocusGroupSessionMutation.isSuccess
    const isError = isErrorPastSessionResponse ||   submitFocusGroupSessionMutation.isError
    const error = errorPastSessionResponse || submitFocusGroupSessionMutation.error
    const isSubmitting = submitFocusGroupSessionMutation.isPending

    return {
        submitFocusGroupSession,
        pastSessionResponse,
        isLoading,
        isSuccess,
        isError,
        error,
        isSubmitting
    };
};


export const useFocusGroupCommunitySessions = (communityId?: string) => {
    const {
        data: focusGroupSessions,
        isLoading: isLoadingFocusGroupSessions,
        isError: isErrorFocusGroupSessions,
        error: errorFocusGroupSessions
    } = useQuery({
        queryKey: ['focusGroupSessions', communityId],
        queryFn: async () => {
            const response = await getFocusGroupSessions();
            return response ? response : []
        }
    });

    return {
        focusGroupSessions,
        isLoadingFocusGroupSessions,
        isErrorFocusGroupSessions,
        errorFocusGroupSessions
    };  
};


export const useFocusGroupFormattedSessions = (communityId?: string) => {
    const { focusGroupSessions } = useFocusGroupCommunitySessions(communityId);
    const formattedFocusGroupSessions = useMemo(() => 
        focusGroupSessions ? focusGroupSessions.map(session => ({
            ...session,
            sessionDate: moment(session.sessionDate),
            sections: session.sections.map(section => ({
              ...section,
              icon: iconMap[section.icon],
              questions: section.questions.map(question => ({
                ...question,
                type: question.type as QuestionType
              }))
            }))
          })) : [],
        [focusGroupSessions]
    );
    return {formattedFocusGroupSessions};
}
